import { components } from "@/api-schema";
import { CompanyType, RelocationOrderTransportMethod } from "@/api-schema-enums";
import { useFitSettingsGetQuery } from "@/common/api/fit-settings/use-fit-settings-get.query";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { BranchTable } from "@/components/data-table/predefined/branch-table";
import { branchTableDefaultColumns } from "@/components/data-table/predefined/branch-table-columns";
import { DataTableHeightMode, ImplementedColumnDef } from "@/components/data-table/types";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { BuildingIcon } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useLocation } from "wouter";
import {
  RelocationOrderType,
  useRelocationOrderInitStore,
} from "@/modules/fit/internal/relocation-order/init/_hooks/use-relocation-order-init-store";
import { useUserAssignedBranches } from "@/modules/fit/internal/relocation-order/init/_hooks/use-user-assigned-branches";

type Branch = components["schemas"]["Branch"] & RowWithIdentifier;

const EXCLUDED_DESTINATION_COMPANY_TYPES = [CompanyType.Transport];

const SELECT_DESTINATION_BRANCH_COLUMNS = [
  branchTableDefaultColumns.columnName,
  branchTableDefaultColumns.columnCompany,
  branchTableDefaultColumns.columnAddress,
  branchTableDefaultColumns.columnBrands,
] as ImplementedColumnDef<Branch, unknown>[];

export function InitSelectDestinationBranchView() {
  const { t } = useTranslation(["common", "fit"]);
  const [_, navigate] = useLocation();

  const hasHydrated = useRelocationOrderInitStore((state) => state.hasHydrated);
  const vehicle = useRelocationOrderInitStore((state) => state.vehicle);
  const relocationOrderType = useRelocationOrderInitStore((state) => state.relocationOrderType);
  const originExternalAddress = useRelocationOrderInitStore((state) => state.originExternalAddress);
  const transportMethod = useRelocationOrderInitStore((state) => state.transportMethod);
  const setDestinationBranch = useRelocationOrderInitStore((state) => state.setDestinationBranch);
  const {
    data: fitSettings,
    isLoading: fitSettingsAreLoading,
    isError: fitSettingsLoadFailed,
  } = useFitSettingsGetQuery();
  const bcaCompanyUuid = fitSettings?.bcaCompany?.uuid;

  const { userBranchUuids, userBranchesTechnicalIds } = useUserAssignedBranches();

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    if (!vehicle) {
      navigate("~/fit/relocation-order/init/select-vehicle", { replace: true });
      return;
    }

    if (relocationOrderType === RelocationOrderType.ExternalAddressToBranch && !originExternalAddress) {
      navigate("~/fit/relocation-order/init/set-origin-external-address", { replace: true });
    }
  }, [hasHydrated, navigate, originExternalAddress, relocationOrderType, vehicle]);

  const currentVehicleBranchUuid = vehicle?.currentBranch?.uuid;
  const vehicleIsAtUserBranch = currentVehicleBranchUuid ? userBranchUuids.includes(currentVehicleBranchUuid) : false;

  const branchSearchBody = useMemo(() => {
    if (relocationOrderType === RelocationOrderType.ExternalAddressToBranch) {
      return { technicalId: userBranchesTechnicalIds, excludeCompanyTypes: EXCLUDED_DESTINATION_COMPANY_TYPES };
    }

    if (relocationOrderType === RelocationOrderType.BranchToBranch && currentVehicleBranchUuid) {
      if (vehicleIsAtUserBranch) {
        return { excludeUuids: [currentVehicleBranchUuid], excludeCompanyTypes: EXCLUDED_DESTINATION_COMPANY_TYPES };
      }

      return {
        technicalId: userBranchesTechnicalIds,
        excludeUuids: [currentVehicleBranchUuid],
        excludeCompanyTypes: EXCLUDED_DESTINATION_COMPANY_TYPES,
      };
    }
  }, [relocationOrderType, currentVehicleBranchUuid, userBranchesTechnicalIds, vehicleIsAtUserBranch]);

  const canRenderDestinationSelection =
    hasHydrated &&
    !fitSettingsAreLoading &&
    !!vehicle &&
    (relocationOrderType !== RelocationOrderType.ExternalAddressToBranch || !!originExternalAddress);

  const handleRowClick = useCallback(
    (branch: Branch) => {
      if (fitSettingsLoadFailed && transportMethod === RelocationOrderTransportMethod.OwnAxle) {
        toast.error(t("fit:FitSettingsLoadError"));
        return;
      }

      const isBcaBranch = !!bcaCompanyUuid && branch.company?.uuid === bcaCompanyUuid;
      if (isBcaBranch && transportMethod === RelocationOrderTransportMethod.OwnAxle) {
        toast.error(t("fit:BcaRequiresTransportCompany"));
        return;
      }

      setDestinationBranch(branch);

      navigate("~/fit/relocation-order/init/set-details");
    },
    [bcaCompanyUuid, fitSettingsLoadFailed, navigate, setDestinationBranch, t, transportMethod],
  );

  if (!canRenderDestinationSelection) {
    return null;
  }

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Table}
      title={t("fit:SelectDestinationBranch")}
      subTitle={t("fit:SelectDestinationBranchDescription")}
    >
      <BranchTable
        heightMode={DataTableHeightMode.Content}
        columns={SELECT_DESTINATION_BRANCH_COLUMNS}
        showCreateButton={false}
        customRowActions={null}
        onRowClick={handleRowClick}
        features={{ expansion: false }}
        subComponent={null}
        body={branchSearchBody}
        noDataIcon={BuildingIcon}
        noDataTitle={t("fit:NoBranchesAvailable")}
        noDataDescription={t("fit:NoDestinationBranchesFound")}
        noResultsTitle={t("fit:NoBranchFound")}
        noResultsDescription={t("fit:NoBranchNearbyPleaseSelectManually")}
      />
    </CenteredLayout>
  );
}
